import styled from 'styled-components';
import { useLocalStorage } from 'react-use';
import {
  IconFlag,
  PopoverOnboarding,
  spacer24,
  spacer8,
  Type,
} from '@spotify-internal/encore-web';
import { useT } from '@spotify-internal/i18n-core';
import { useHeldReleases } from '@hooks/useHeldReleases';
import { NavBarListItemAsLink } from '@components/shared/NavBarListItemAsLink';
import { ErrorType } from '@generated/grpc/spotify/artistwatchlist/v1/ErrorType';
import { _spotify_s4mpview_v1_WhoAmIResponse_NamedEntity__Output } from '@generated/grpc/spotify/s4mpview/v1';

interface HeldInReviewTabProps {
  isActive: boolean;
  hrefParam: string;
  handleHeldReleasesTabClickGAEvent: () => void;
  userLicensors: _spotify_s4mpview_v1_WhoAmIResponse_NamedEntity__Output[];
}

const StyledSpan = styled.span`
  padding-right: ${spacer8};
`;

const StyledPopoverOnboarding = styled(PopoverOnboarding)`
  position: absolute;
  margin-top: ${spacer24};
  z-index: 1;
`;

const StyledType = styled(Type)`
  display: flex;
  align-items: center;
`;

export const HeldInReviewNavBar = ({
  isActive,
  hrefParam,
  handleHeldReleasesTabClickGAEvent,
  userLicensors,
}: HeldInReviewTabProps) => {
  const t = useT();
  const [heldInReviewOnboardingVisible, setHeldInReviewOnboardingVisible] =
    useLocalStorage('heldInReviewOnboardingVisible', true);
  const { data, isLoading } = useHeldReleases(
    userLicensors.map(licensor => licensor.id),
    1,
    1,
    false,
  );

  const HELD_RELEASES_POPOVER_TITLE = t(
    'atlas.held_in_review_popover_title',
    'Request approval for products held in review',
    'Title for Held in review popover',
  );

  const HELD_IN_REVIEW_POPOVER_TEXT = t(
    'atlas.held_in_review_popover_text',
    'Products are sometimes held in review for different reasons which prevent them from going live. Request approval for products as soon as you can. Once approved it can take up to 5 business days to verify approvals and may delay them going live on time.',
    'Text for Held in review popover',
  );

  const HELD_IN_REVIEW_TEXT = t(
    'atlas.held_in_review',
    'Held in review',
    'Title for the Held in review section',
  );

  const hasNoPermission =
    !data || data.error?.type === ErrorType.NOT_AUTHORIZED;
  const albumMetadata = data?.albumMetadataListResponse?.albumMetadata ?? [];
  const heldReleasesNeedToBeReviewed = albumMetadata.length > 0;

  const onCloseHeldInReviewModal = () => {
    setHeldInReviewOnboardingVisible(false);
  };

  if (isLoading || hasNoPermission) {
    return <></>;
  }

  return (
    <NavBarListItemAsLink
      active={isActive}
      href={hrefParam}
      onClick={handleHeldReleasesTabClickGAEvent}
      sentenceCase
    >
      <StyledType variant="celloCanon">
        <StyledSpan>
          {HELD_IN_REVIEW_TEXT}{' '}
          {heldReleasesNeedToBeReviewed && (
            <IconFlag size="small" title={HELD_IN_REVIEW_TEXT} />
          )}
        </StyledSpan>
      </StyledType>
      {heldInReviewOnboardingVisible && (
        <StyledPopoverOnboarding
          id="heldInReviewPopover"
          arrow="topStart"
          popoverTitle={HELD_RELEASES_POPOVER_TITLE}
          colorSet="mutedAccent"
          onClose={onCloseHeldInReviewModal}
          onClick={(event: React.MouseEvent) => {
            event.preventDefault();
            event.stopPropagation();
          }}
        >
          {HELD_IN_REVIEW_POPOVER_TEXT}
        </StyledPopoverOnboarding>
      )}
    </NavBarListItemAsLink>
  );
};
